import { Check, Minus } from "lucide-react";
import {
  Container,
  Section,
  Heading,
  Lede,
  Eyebrow,
  Card,
  LinkButton,
  Badge,
} from "../components/ui";
import { useSeo } from "../lib/seo";

const TIERS: Array<{
  name: string;
  tagline: string;
  highlight?: boolean;
  bullets: string[];
  cta: string;
}> = [
  {
    name: "Starter",
    tagline: "For solo consultant pharmacists.",
    bullets: [
      "1 user · up to 3 facilities",
      "All clinical rule packs",
      "Adobe Sign embedded signing",
      "QuickBooks invoicing",
      "Email reminders",
      "99.5% uptime SLA",
      "BAA signed",
    ],
    cta: "Talk to sales",
  },
  {
    name: "Professional",
    tagline: "For multi-pharmacist practices.",
    highlight: true,
    bullets: [
      "Up to 10 users · unlimited facilities",
      "Everything in Starter",
      "AI synthesis + learning loop",
      "SMS reminders (Twilio)",
      "Calendar sync (Google + Outlook)",
      "Assessment worksheets",
      "State board filings",
      "99.9% uptime SLA",
    ],
    cta: "Talk to sales",
  },
  {
    name: "Enterprise",
    tagline: "For health systems and MSOs.",
    bullets: [
      "Unlimited users · multi-tenant",
      "Everything in Professional",
      "Custom rule pack development",
      "SSO (SAML / OIDC)",
      "Dedicated success manager",
      "Custom retention windows",
      "Custom subprocessor review",
      "99.99% uptime SLA",
    ],
    cta: "Talk to sales",
  },
];

const COMPARISON: Array<{ label: string; tiers: [boolean, boolean, boolean] | [string, string, string] }> = [
  { label: "Clinical rule packs", tiers: [true, true, true] },
  { label: "Adobe Sign workflows", tiers: [true, true, true] },
  { label: "QBO invoicing", tiers: [true, true, true] },
  { label: "Email reminders", tiers: [true, true, true] },
  { label: "AI synthesis (Bedrock)", tiers: [false, true, true] },
  { label: "SMS reminders", tiers: [false, true, true] },
  { label: "Calendar sync", tiers: [false, true, true] },
  { label: "Assessment worksheets", tiers: [false, true, true] },
  { label: "State board filings", tiers: [false, true, true] },
  { label: "SSO (SAML / OIDC)", tiers: [false, false, true] },
  { label: "Custom rule development", tiers: [false, false, true] },
  { label: "Uptime SLA", tiers: ["99.5%", "99.9%", "99.99%"] },
];

export function Pricing() {
  useSeo({
    title: "Pricing — WeConsult Rx",
    description:
      "Three tiers. Starter for solo pharmacists, Professional for practices, Enterprise for health systems. All tiers include BAA and HIPAA controls.",
    path: "/pricing",
  });

  return (
    <>
      <Section className="pb-8">
        <Container>
          <div className="max-w-3xl">
            <Eyebrow>Pricing</Eyebrow>
            <Heading level={1} className="mt-3">
              Built for consultant pharmacists. Priced per practice.
            </Heading>
            <Lede className="mt-6">
              Custom pricing based on facility count and feature mix. Every tier
              ships with HIPAA controls and a BAA — no upsell tax for compliance.
            </Lede>
          </div>
        </Container>
      </Section>

      <Section className="pt-8">
        <Container>
          <div className="grid gap-6 lg:grid-cols-3">
            {TIERS.map((t) => (
              <Card
                key={t.name}
                className={`relative h-full ${
                  t.highlight ? "border-forest-700 ring-1 ring-forest-700" : ""
                }`}
              >
                {t.highlight ? (
                  <Badge className="absolute -top-3 left-6">Most popular</Badge>
                ) : null}
                <Heading level={3}>{t.name}</Heading>
                <p className="mt-2 text-ink-600">{t.tagline}</p>
                <div className="mt-6 text-3xl font-extrabold tracking-tightest">
                  Contact for pricing
                </div>
                <ul className="mt-6 space-y-2 text-sm text-ink-700">
                  {t.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2">
                      <Check className="h-4 w-4 text-forest-700 mt-0.5" aria-hidden />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-8">
                  <LinkButton to="/demo" variant={t.highlight ? "primary" : "secondary"} className="w-full justify-center">
                    {t.cta}
                  </LinkButton>
                </div>
              </Card>
            ))}
          </div>
        </Container>
      </Section>

      <Section tone="soft">
        <Container>
          <Heading level={2}>Feature comparison</Heading>
          <div className="mt-8 overflow-x-auto rounded-2xl border border-border bg-surface">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left">
                  <th className="px-5 py-3 font-semibold text-ink-700 w-1/2">Feature</th>
                  <th className="px-5 py-3 font-semibold text-ink-700 text-center">Starter</th>
                  <th className="px-5 py-3 font-semibold text-ink-700 text-center bg-forest-50">Professional</th>
                  <th className="px-5 py-3 font-semibold text-ink-700 text-center">Enterprise</th>
                </tr>
              </thead>
              <tbody>
                {COMPARISON.map((row, i) => (
                  <tr key={row.label} className={i % 2 === 0 ? "bg-canvas/50" : ""}>
                    <td className="px-5 py-3 text-ink-800">{row.label}</td>
                    {row.tiers.map((t, idx) => (
                      <td
                        key={idx}
                        className={`px-5 py-3 text-center ${idx === 1 ? "bg-forest-50/50" : ""}`}
                      >
                        {typeof t === "boolean" ? (
                          t ? (
                            <Check className="h-4 w-4 text-forest-700 mx-auto" aria-label="Yes" />
                          ) : (
                            <Minus className="h-4 w-4 text-ink-300 mx-auto" aria-label="No" />
                          )
                        ) : (
                          <span className="text-ink-800 font-semibold">{t}</span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Container>
      </Section>

      <Section className="text-center">
        <Container>
          <Heading level={2}>Ready to talk?</Heading>
          <p className="mt-4 text-ink-600 max-w-2xl mx-auto">
            Book a 30-minute call. Bring a sample chart. We’ll walk you through
            it live.
          </p>
          <div className="mt-8">
            <LinkButton to="/demo" size="lg">
              Request a demo
            </LinkButton>
          </div>
        </Container>
      </Section>
    </>
  );
}
